import { Autocomplete, Box, FormControl, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import { Currency } from 'models/country';
import React, { FC, useState } from 'react';
import * as currencies from 'currency.json';
import { useTranslation } from 'react-i18next';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

type CurrencyInputProps = {
    currency: Currency;
    onCurrencySelect: (currency: Currency) => void;
    disabled?: boolean;
};

const CurrencyInput: FC<CurrencyInputProps> = ({ currency, onCurrencySelect, disabled }: CurrencyInputProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [currencyOptions, setCurrencyOptions] = useState<Currency[]>(Object.values(currencies));
    const [inputValue, setInputValue] = useState('');

    return (
        <FormControl fullWidth>
            <Autocomplete
                fullWidth
                autoComplete
                disableClearable
                disabled={disabled}
                options={currencyOptions || []}
                getOptionLabel={(currencyObj: Currency) => currencyObj.code || ''}
                filterOptions={(x) => x}
                isOptionEqualToValue={(currencyObj: Currency) => currencyObj.code === currency.code}
                includeInputInList
                noOptionsText={translate('orders:TYPE_TO_SEARCH')}
                renderOption={(props, currencyObj: Currency) => {
                    const matches = match(currencyObj.code || '', inputValue);
                    const parts = parse(currencyObj.code || '', matches);
                    return (
                        <MenuItem {...props} key={currencyObj.code} value={currencyObj.code}>
                            <ListItemText>
                                {parts.map((part, index) => {
                                    return (
                                        <Box
                                            key={index}
                                            component="span"
                                            sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                        >
                                            {part.text}
                                        </Box>
                                    );
                                })}
                                <Typography variant="body2" color="grey.700">
                                    {currencyObj.name}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    );
                }}
                renderInput={(params) => <TextField {...params} label={translate('common:SELECT_CURRENCY')} />}
                inputValue={inputValue}
                value={currency}
                onChange={(_, currency: Currency) => onCurrencySelect(currency)}
                onInputChange={(_, value) => {
                    setInputValue(value);
                    const options = Object.values(currencies).filter((currencyObj) => {
                        return currencyObj?.code?.toLowerCase().includes(value?.toLowerCase());
                    });
                    setCurrencyOptions(options);
                }}
            />
        </FormControl>
    );
};

export default CurrencyInput;
